import React, { useState, useEffect } from "react";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch } from "react-redux";
import { getUserCredential } from "../../../lib/cookies";
import { generateHeader } from "src/api-setup/api-helper";
import { POSTAPI } from "src/api-setup/api-helper";
import { GET_TIMESHEET_DETAILS, INSERT_TIMESHEET_APPROVAL } from "src/api-setup/api-endpoints";
import { setNewMessage } from "../../../redux/actions/setting-actions";
import "./PendingApprovalDialog.scss";
import { TextArea } from "@progress/kendo-react-inputs";
import { LoadingPanel } from "src/components/loadingPanel";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadFile } from "src/pages/messages/service/downloadFile";
import FileViewerDialog from "./FileViewerDialog";
import ConfirmDialog from "./ConfirmDialog";
import { DialogComponent } from "src/components/dialog";
import { APPROVE_BTN_ACTIONS, REJECT_BTN_ACTIONS } from "src/constants/constants";
import ModalTitle from "./ModalTitle";
import ErrorDisplay from "src/components/error-display/ErrorDisplay";
import { convertTimeZone } from "src/lib/date-time.util";
import { formatTimesheetQuantity } from "src/pages/providerPayRequestDashboard/utils/thresholdQuantity";

const PendingApprovalDialog = ({ visible, onClose, providerId, rowData, onRefresh, onHold }) => {
  const dispatch = useDispatch();
  const cookiesData = getUserCredential();

  const [timesheetData, setTimesheetData] = useState(null);
  const [isTimesheetLoading, setIsTimesheetLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState("");
  const [isNotFoundError, setIsNotFoundError] = useState(false);
  // const [rejectDialogVisible, setRejectDialogVisible] = useState(false)
  // const [approveDialogVisible, setApproveDialogVisible] = useState(false)
  const [timesheetMstrKey, seTtimesheetMstrKey] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [fileViewerVisible, setFileViewerVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [confirmationPopup, setConfirmationPopup] = React.useState({
    title: "",
    desc: "",
    isVisible: false,
    type: "",
    actionBtn: [],
    data: {},
    action: "",
  });

  const fetchTimesheetDetails = async () => {
    if (!cookiesData?.secretToken || !rowData.timesheet_mstr_key) return;
    setIsTimesheetLoading(true);

    try {
      const apiHeaders = generateHeader();
      const requestBody = {
        in_tenant_id: cookiesData.out_tenant_id,
        in_timesheet_mstr_key: rowData.timesheet_mstr_key,
        in_user_key: cookiesData.out_user_id,
        in_user_type: "APPROVER",
        in_filter: null,
        in_sort_number: 1,
        in_sort_order: "ASC",
      };

      const response = await POSTAPI(false, GET_TIMESHEET_DETAILS, null, apiHeaders, requestBody, dispatch);

      if (response?.data?.row_result && response.data.row_result.length > 0) {
        const timesheetData = response.data.row_result[0];
        setTimesheetData(timesheetData);
        const formattedTimesheetKey = [
          {
            id: timesheetData.timesheet_mstr_key,
          },
        ];
        seTtimesheetMstrKey(formattedTimesheetKey);
        setIsTimesheetLoading(false);
        setIsNotFoundError(false);
      } else {
        dispatch(
          setNewMessage({
            message: "No timesheet data found",
            type: "error",
          })
        );
        setIsNotFoundError(true);
        setIsTimesheetLoading(false);
      }
    } catch (error) {
      setIsNotFoundError(true);
      console.error("Error fetching timesheet details:", error);
      dispatch(
        setNewMessage({
          message: "Error fetching timesheet details",
          type: "error",
        })
      );
      setIsTimesheetLoading(false);
    }
  };

  useEffect(() => {
    fetchTimesheetDetails();
  }, [visible, rowData]);

  const handleApprove = () => {
    //  setApproveDialogVisible(true)
    setConfirmationPopup({
      ...confirmationPopup,
      title: "Confirm Approval",
      desc: "Are you sure you want to approve the selected pay request?",
      isVisible: true,
      type: "APPROVE_CONFIRM",
      actionBtn: APPROVE_BTN_ACTIONS(onCancelClick, () => onSubmitClick("4")),
      action: "",
    });
  };

  const handleReject = () => {
    // setRejectDialogVisible(true)
    setConfirmationPopup({
      ...confirmationPopup,
      title: "Confirm Approval",
      desc: "Are you sure you want to reject the selected pay request?",
      isVisible: true,
      type: "REJECT_CONFIRM",
      actionBtn: REJECT_BTN_ACTIONS(onCancelClick, () => onSubmitClick("3")),
      action: "",
    });
  };

  const handleClose = () => {
    onClose();
  };

  const onCancelClick = () => {
    // setRejectDialogVisible(false)
    // setApproveDialogVisible(false)
    setConfirmationPopup({
      ...confirmationPopup,
      isVisible: false,
      type: "",
      actionBtn: [],
      data: {},
      action: "",
    });
  };

  const onSubmitClick = async (status) => {
    if (!cookiesData?.out_user_id) return;
    setIsSubmitting(true);

    try {
      const apiHeaders = { "x-access-token": cookiesData.secretToken };
      const requestBody = {
        in_user_id: cookiesData.out_user_id,
        in_tenant_id: cookiesData.out_tenant_id,
        in_timesheet_mstr_key: timesheetMstrKey,
        in_approver_mstr_key: cookiesData.out_user_id.toString(),
        in_comment: comments,
        in_status: status,
      };

      const response = await POSTAPI(false, INSERT_TIMESHEET_APPROVAL, null, apiHeaders, requestBody, dispatch);

      const outParameters = response?.data?.out_parameters;
      const landedOnHold = status === "4" && (outParameters?.heldTimesheetMstrKey?.length || 0) > 0;
      const actionSucceeded = outParameters?.out_ret_code === 0;

      if (landedOnHold) {
        onHold?.();
      } else if (actionSucceeded) {
        const message = status === "4" ? "Timesheet approved successfully" : "Timesheet rejected successfully";
        dispatch(setNewMessage({ message: message, type: "Success", show: true }));
      }
      if (landedOnHold || actionSucceeded) {
        onRefresh();
      }
    } catch (error) {
      console.log(error, "error");
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  // Utility function to get file icon based on MIME type
  const getFileIcon = (mimeType) => {
    if (!mimeType) return "fa-file";

    const type = mimeType.toLowerCase();
    if (type.startsWith("image/")) return "fa-file-image";
    if (type === "application/pdf") return "fa-file-pdf";
    if (type.includes("word") || type.includes("document")) return "fa-file-word";
    if (type.includes("excel") || type.includes("spreadsheet")) return "fa-file-excel";
    if (type.includes("text")) return "fa-file-alt";
    return "fa-file";
  };

  // Handle file view
  const handleFileView = (file) => {
    setSelectedFile(file);
    setFileViewerVisible(true);
  };

  // Handle file download
  const handleFileDownload = async (file) => {
    try {
      setDownloadProgress((prev) => ({ ...prev, [file.document_name]: 0 }));

      await downloadFile(file, (percentage) => {
        setDownloadProgress((prev) => ({ ...prev, [file.document_name]: percentage }));
      });

      // Clear progress after a delay
      setTimeout(() => {
        setDownloadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[file.document_name];
          return newProgress;
        });
      }, 1000);
    } catch (error) {
      console.error("Error downloading file:", error);
      dispatch(
        setNewMessage({
          message: "Failed to download file",
          type: "error",
        })
      );
      setDownloadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.document_name];
        return newProgress;
      });
    }
  };

  // Utility function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };
  return (
    <Window
      key={`window-${visible}`}
      visible={visible}
      onClose={handleClose}
      title={
        <ModalTitle
          title={
            !isTimesheetLoading &&
            `${timesheetData?.status?.display_name} approvals`
          }
        />
      }
      width={550}
      height={650}
      className="view-approve-pay-request-dialog"
      modal={true}
      closeIcon={true}
      backdrop={true}
    >
      <div className="dialog-content">
        {!isTimesheetLoading && isNotFoundError ? (
          <ErrorDisplay
            icon="fa-exclamation-circle"
            title="No Pay Request Found"
            description="The pay request you are looking for does not exist."
          />
        ) : isTimesheetLoading ? (
          <LoadingPanel gridId="view-approve-pay-request-container" />
        ) : (
          <>
            <div className="view-approve-pay-request-container border-container">
              <div className="view-pay-request-provider">
                <div className="view-pay-request-provider-icon">
                  <i className="fas fa-user"></i>
                </div>
                <div className="view-pay-request-provider-container">
                  <div className="field-label">{timesheetData?.provider_name}</div>
                  <div className="field-value">{timesheetData?.position_type_name}</div>
                </div>
              </div>
            </div>
            <div className="view-approve-pay-request-container border-container">
              <div className="view-pay-request-description">
                <div className="field-label">Location</div>
                <div className="field-value">{timesheetData?.location_name}</div>
              </div>
            </div>
            <div className="view-approve-pay-request-container">
              <div className="view-pay-request-location border-container">
                <div className="field-label">Pay Request Type</div>
                <div className="field-value">{timesheetData?.contract_type}</div>
              </div>
              <div className="view-pay-request-specialty border-container">
                <div className="field-label">Pay Request Name</div>
                <div className="field-value">{timesheetData?.contract_name}</div>
              </div>
            </div>

            {timesheetData?.impersonator_user_name !== null && timesheetData?.impersonator_user_name ? (
              <div className="view-approve-pay-request-container border-container">
                <div className="view-pay-request-description">
                  <div className="field-label">Submitted By</div>
                  <div className="field-value">{timesheetData?.impersonator_user_name ?? "NA"}</div>
                </div>
              </div>
            ) : null}

            <div className="view-approve-pay-request-container">
              <div className="view-pay-request-type border-container">
                <div className="field-label">Total Quantity</div>
                <div className="field-value">
                  {!isTimesheetLoading && formatTimesheetQuantity(timesheetData)}
                </div>
              </div>
              <div className="view-pay-request-name border-container">
                <div className="field-label">Total Amount</div>
                <div className="field-value">{!isTimesheetLoading && `$ ${timesheetData?.budget.toFixed(2)}`}</div>
              </div>
            </div>
            <div className="view-approve-pay-request-container">
              <div className="view-pay-request-service-date border-container">
                <div className="field-label">Service Date</div>
                <div className="field-value">{timesheetData?.service_date ? convertTimeZone({ date: timesheetData.service_date, convertTimeZone: timesheetData?.local_timezone || "America/New_York", outputFormat: "MM/dd/yyyy" }) || "" : ""}</div>
              </div>
              <div className="view-pay-request-post-date border-container">
                <div className="field-label">Post Date</div>
                <div className="field-value">{timesheetData?.post_date ? convertTimeZone({ date: timesheetData.post_date, convertTimeZone: timesheetData?.local_timezone || "America/New_York", outputFormat: "MM/dd/yyyy" }) || "" : ""}</div>
              </div>
            </div>

            {timesheetData?.documents && timesheetData.documents.length > 0 && (
              <div className="view-approve-pay-request-container border-container">
                <div className="view-pay-request-documents">
                  <div className="field-label">Documents</div>
                  <div className="documents-list">
                    {timesheetData.documents.map((doc, index) => (
                      <div key={index} className="document-item">
                        <div className="document-info">
                          <i className={`fas ${getFileIcon(doc.document_type)} document-icon`}></i>
                          <div className="document-details">
                            <div className="document-name">{doc.original_name}</div>
                            <div className="document-size">{formatFileSize(doc.document_size)}</div>
                          </div>
                        </div>
                        <div className="document-actions">
                          <Tooltip anchorElement="target" position="top" openDelay={100}>
                            <Button
                              className="document-action-btn view-btn"
                              onClick={() => handleFileView(doc)}
                              title="View file"
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                          </Tooltip>
                          <Tooltip anchorElement="target" position="top" openDelay={100}>
                            <Button
                              className="document-action-btn download-btn"
                              onClick={() => handleFileDownload(doc)}
                              title="Download file"
                              disabled={
                                downloadProgress[doc.document_name] !== undefined &&
                                downloadProgress[doc.document_name] < 100
                              }
                            >
                              {downloadProgress[doc.document_name] !== undefined &&
                              downloadProgress[doc.document_name] < 100 ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-download"></i>
                              )}
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="view-approve-pay-request-container border-container">
              <div className="view-pay-request-description">
                <div className="field-label">Description</div>
                <div className="field-value" style={{ color: timesheetData?.ts_description ? "" : "#999999" }}>
                  {timesheetData?.ts_description ? timesheetData?.ts_description : "No description"}
                </div>
              </div>
            </div>

            <div className="view-approve-pay-request-container border-container">
              <div className="view-pay-request-description">
                <div className="field-label">Comments</div>
                <div className="field-value">
                  {timesheetData?.status?.timesheet_status_mstr_key === 1 ? (
                    <TextArea
                      value={comments}
                      onChange={(e) => setComments(e.value)}
                      placeholder="Enter Comments"
                      className="description-textarea"
                      rows={3}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <>
                      {timesheetData?.comments
                        ?.map((it) => `${it.approver}: ${it.comment === null ? "N/A" : it.comment}`)
                        ?.join("\n") ?? ""}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {timesheetData?.status?.timesheet_status_mstr_key === 1 && !isTimesheetLoading && !isNotFoundError && (
        <div className="action-buttons-container">
          <Button className="k-button cancel-button" onClick={() => handleReject()}>
            Reject
          </Button>
          <Button className="k-button submit-button" onClick={() => handleApprove()}>
            Approve
          </Button>
        </div>
        // <WindowActionsBar className='k-window-buttongroup' layout={'end'}>
        //     <Button type={'submit'} className="k-button cancelbtn" onClick={handleReject}>
        //         Reject
        //     </Button>
        //     <Button
        //         className="primarybtn k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
        //         onClick={handleApprove}
        //     >
        //         Approve
        //     </Button>
        // </WindowActionsBar>
      )}

      <DialogComponent
        title={confirmationPopup.title}
        onCancelConfirm={onCancelClick}
        width={"450"}
        height={"auto"}
        disabled={false}
        className={"Dialog-Approve-Reject"}
        desc={confirmationPopup.desc}
        visible={confirmationPopup.isVisible}
        actionBtn={confirmationPopup.actionBtn}
        titleClassName={confirmationPopup.action === "approve" ? "approve-confirm" : "reject-confirm"}
        closeIcon={false}
      />

      {/* {rejectDialogVisible && (
                <ConfirmDialog
                    visible={rejectDialogVisible}
                    description="Are you sure you want to reject the selected pay request?"
                    btnCancel={'Cancel'}
                    btnSubmit={'Reject'}
                    onCancelClick={onCancelClick}
                    onSubmitClick={() => onSubmitClick('REJECTED')}
                    className="reject"
                    title="Confirm Rejection"
                />
            )}

            {approveDialogVisible && (
                <ConfirmDialog
                    visible={approveDialogVisible}
                    description="Are you sure you want to approve the selected pay request?"
                    btnCancel={'Cancel'}
                    btnSubmit={'Approve'}
                    onCancelClick={onCancelClick}
                    onSubmitClick={() => onSubmitClick('APPROVED')}
                    className="approve"
                    title="Confirm Approval"
                />
            )} */}
      {fileViewerVisible && (
        <FileViewerDialog
          visible={fileViewerVisible}
          onClose={() => {
            setFileViewerVisible(false);
            setSelectedFile(null);
          }}
          file={selectedFile}
        />
      )}
    </Window>
  );
};

export default PendingApprovalDialog;
